import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Alert } from '@material-ui/lab';
import { useFieldFast } from '../../form/hooks';
import { IdPropType } from '../../../proptypes/basic';
import { useNurseDeploymentsByCareNeedId } from '../../nurseDeployments/nurseDeploymentsSlice';
import { useNurseRequestsByCareNeedId } from '../../nurseRequests/nurseRequestsSlice';
import { selectAllAgencies } from '../../agencies/agenciesSlice';
import { formatDate } from '../../date/utils';

/**
 * Shown in "BK-Vorschlag bearbeiten" as soon as a rejection date is entered for a
 * suggestion that was already confirmed - typically the BK cancelling days later
 * (docs/plans/care-need-single-agency.md, rule 8). It then no longer counts as
 * confirmed, but two things don't happen by themselves: a BK-Einsatz already created
 * for it stays, and the other agencies' BK-Anfragen may have been closed already.
 */
const CancelledConfirmationHint = ({ nurseSuggestionId, careNeedId }) => {
    const [{ value: confirmedAt }] = useFieldFast('confirmedAt');
    const [{ value: rejectedAt }, { initialValue: initialRejectedAt }] = useFieldFast('rejectedAt');
    const [deployments] = useNurseDeploymentsByCareNeedId(careNeedId);
    const [nurseRequests] = useNurseRequestsByCareNeedId(careNeedId);
    const agencies = useSelector(selectAllAgencies);

    const suggestionDeployments = useMemo(
        () =>
            (deployments || []).filter(
                (deployment) =>
                    nurseSuggestionId &&
                    String(deployment.nurseSuggestionId) === String(nurseSuggestionId)
            ),
        [deployments, nurseSuggestionId]
    );

    const openRequestAgencies = useMemo(
        () =>
            (nurseRequests || [])
                .filter((request) => !request.closedAt)
                .map((request) => agencies.find((agency) => agency.id === request.agencyId))
                .filter((agency, index, self) => agency && self.indexOf(agency) === index)
                .map((agency) => agency.matchCode || agency.name),
        [nurseRequests, agencies]
    );

    // only while the rejection is being entered - not for one saved long ago
    if (!confirmedAt || !rejectedAt || initialRejectedAt) return null;

    return (
        <Alert severity="warning">
            Diese BK war bereits vom Kunden bestätigt. Mit der Ablehnung zählt sie nicht mehr als
            bestätigt.
            <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                {suggestionDeployments.length > 0 ? (
                    <li>
                        Für diese BK gibt es bereits{' '}
                        {suggestionDeployments.length === 1 ? 'einen BK-Einsatz' : 'BK-Einsätze'} (
                        {suggestionDeployments
                            .map(
                                (deployment) =>
                                    `${formatDate(deployment.careFrom)} bis ${formatDate(
                                        deployment.careTill
                                    )}`
                            )
                            .join(', ')}
                        ). Findet er nicht statt, bitte in der Kundenakte unter „BK-Einsätze“
                        löschen.
                    </li>
                ) : (
                    <li>Für diese BK ist kein BK-Einsatz angelegt.</li>
                )}
                {openRequestAgencies.length > 0 ? (
                    <li>
                        Für diesen BK-Bedarf sind noch BK-Anfragen offen (
                        {openRequestAgencies.join(', ')}).
                    </li>
                ) : (
                    <li>
                        Für diesen BK-Bedarf ist keine BK-Anfrage mehr offen – für Ersatz bitte im
                        Einsatzboard neue BK-Anfragen versenden.
                    </li>
                )}
            </ul>
        </Alert>
    );
};

CancelledConfirmationHint.propTypes = {
    nurseSuggestionId: IdPropType,
    careNeedId: IdPropType,
};

CancelledConfirmationHint.defaultProps = {
    nurseSuggestionId: null,
    careNeedId: null,
};

export default CancelledConfirmationHint;
